import React, { useState, useEffect } from 'react';

const InstagramIcon: React.FC = () => (
  <img src="/instagram.svg" alt="Instagram" width={22} height={22} style={{ display: 'block' }} />
);

const Newsletter: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAlreadySubscribed, setIsAlreadySubscribed] = useState(false);

  // Check if user already subscribed
  useEffect(() => {
    const subscribedEmail = localStorage.getItem('newsletter_subscribed');
    if (subscribedEmail) {
      setIsAlreadySubscribed(true);
      setEmail(subscribedEmail);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Check if already subscribed with this email
    const subscribedEmail = localStorage.getItem('newsletter_subscribed');
    if (subscribedEmail === email.trim().toLowerCase()) {
      setIsAlreadySubscribed(true);
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await fetch('https://4vib7hmrhxpwv7txwlxjo6lweq0lodql.lambda-url.us-east-1.on.aws/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to subscribe');
      }

      const result = await response.json();
      console.log('Newsletter signup success:', result);
      
      // Store email in localStorage to prevent re-submission
      localStorage.setItem('newsletter_subscribed', email.trim().toLowerCase());
      
      setIsSubmitted(true);
      setEmail('');
    } catch (error) {
      console.error('Newsletter signup error:', error);
      alert('Failed to subscribe. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="section bg-gray-900">
      <div className="container" style={{ maxWidth: '800px' }}>
        <div className="text-center">
          <h2 className="text-white" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '24px' }}>
            Stay Connected
          </h2>

          {/* Social follows — Instagram, monochrome (B&W) */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '32px',
            marginBottom: '32px'
          }}>
            <a
              href="https://www.instagram.com/thenatalie_mitchell"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', color: 'white', textDecoration: 'none' }}
            >
              <InstagramIcon />
              <span style={{ fontWeight: 600 }}>@thenatalie_mitchell</span>
            </a>
            <a
              href="https://www.instagram.com/grandcentralpetaluma"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', color: 'white', textDecoration: 'none' }}
            >
              <InstagramIcon />
              <span style={{ fontWeight: 600 }}>@grandcentralpetaluma</span>
            </a>
          </div>

          <p className="text-white" style={{ fontSize: '1.25rem', marginBottom: '48px', opacity: 0.9, lineHeight: '1.6' }}>
            Get weekly insights on public speaking, networking strategies, and presentation excellence delivered directly to your inbox.
          </p>
          
          {isSubmitted ? (
            <div className="success-message" style={{ maxWidth: '500px', margin: '0 auto', padding: '32px', borderRadius: '12px' }}>
              <div style={{ fontSize: '3rem', marginBottom: '16px', color: 'var(--secondary-sage)', fontWeight: 'bold' }}>✓</div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '8px' }}>Thank you for subscribing!</h3>
              <p style={{ fontSize: '1rem', margin: 0 }}>You'll receive our next newsletter with valuable communication tips soon.</p>
            </div>
          ) : isAlreadySubscribed ? (
            <div style={{ 
              maxWidth: '500px', 
              margin: '0 auto', 
              padding: '32px', 
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '2px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '16px', color: 'white', fontWeight: 'bold' }}>@</div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '8px', color: 'white' }}>Already Subscribed!</h3>
              <p style={{ fontSize: '1rem', margin: 0, color: 'rgba(255, 255, 255, 0.9)' }}>
                You're already on our list with {email}. Check your inbox for our latest updates!
              </p>
              <button 
                onClick={() => {
                  localStorage.removeItem('newsletter_subscribed');
                  setIsAlreadySubscribed(false);
                  setEmail('');
                }}
                style={{
                  marginTop: '16px',
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Subscribe with different email
              </button>
            </div>
          ) : (
            <div>
              <form onSubmit={handleSubmit} style={{ maxWidth: '500px', margin: '0 auto' }}>
                <div className="flex" style={{ gap: '16px', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      required
                      className="form-input"
                      style={{ flex: 1, fontSize: '16px' }}
                      disabled={isLoading}
                    />
                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{ 
                        fontSize: '16px',
                        padding: '12px 24px',
                        whiteSpace: 'nowrap',
                        opacity: isLoading ? 0.7 : 1,
                        cursor: isLoading ? 'not-allowed' : 'pointer'
                      }}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Subscribing...' : 'Subscribe'}
                    </button>
                  </div>
                </div>
              </form>
              
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '20px',
                margin: '32px 0',
                maxWidth: '500px',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}>
                <h4 style={{ color: 'white', fontSize: '1rem', fontWeight: '600', marginBottom: '12px' }}>What you'll receive:</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left' }}>
                  <li style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', color: 'white', opacity: 0.9 }}>
                    <span style={{ marginRight: '12px', fontWeight: 'bold' }}>•</span>
                    <span style={{ fontSize: '14px' }}>Weekly communication tips and strategies</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', color: 'white', opacity: 0.9 }}>
                    <span style={{ marginRight: '12px', fontWeight: 'bold' }}>•</span>
                    <span style={{ fontSize: '14px' }}>Exclusive insights from corporate training sessions</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', color: 'white', opacity: 0.9 }}>
                    <span style={{ marginRight: '12px', fontWeight: 'bold' }}>•</span>
                    <span style={{ fontSize: '14px' }}>Early access to new courses and resources</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', color: 'white', opacity: 0.9 }}>
                    <span style={{ marginRight: '12px', fontWeight: 'bold' }}>•</span>
                    <span style={{ fontSize: '14px' }}>No spam, unsubscribe anytime</span>
                  </li>
                </ul>
              </div>
              
              <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '16px' }}>
                We respect your privacy. Your email will only be used for our newsletter.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Newsletter;